import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';

/**
 * Hook which invokes your function only if the component is still mounted.
 *
 * Note that this is unrelated to `useCallback`.
 *
 * This is especially useful for async operations in submit handlers.
 * For example:

    function MyComponent(props: MyProps) {
      const [inFlight, setInFlight] = useStateIfMounted(false);
      const afterSubmit = useFunctionIfMounted<void>(props.onRequestClose);

      async function handleSubmit() {
        try {
          await props.onRequestSubmit();
          afterSubmit();
        } catch (err) {
          setInFlight(false);
          throw err;
        }
      }
    }

 *
 */

export default function useFunctionIfMounted<TParams, TReturn>(
  callback: (...params: Array<TParams>) => TReturn
): (...params: Array<TParams>) => TReturn | null {
  const isMounted = useIsNotUnmounted();

  return (params) => (isMounted.current ? callback(params) : null);
}
